import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, User, MapPin, Mail, Calendar, GitBranch, GitMerge, AlertTriangle, FileText, ChevronRight, HelpCircle, Scale, Star, Shield, ExternalLink } from 'lucide-react';
import { api } from '../services/api';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';

const EXCLUSION_SOURCES = ['EXCLUSION_LIST', 'REGULATORY_LIST'];

export default function EntityDetailPage() {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const [expandedRecords, setExpandedRecords] = useState<Set<string>>(new Set());
  const [reviewingMatch, setReviewingMatch] = useState<number | null>(null);
  const [viewingWhyRelated, setViewingWhyRelated] = useState<number | null>(null);
  const [whyRelatedData, setWhyRelatedData] = useState<Record<number, unknown>>({});

  const toggleRecord = (key: string) => {
    setExpandedRecords((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Mutation to create adjudication item and navigate to it
  const createAdjudicationMutation = useMutation({
    mutationFn: ({ entityId1, entityId2, matchKey }: { entityId1: number; entityId2: number; matchKey?: string }) =>
      api.createAdjudicationItem(entityId1, entityId2, matchKey),
    onSuccess: (data) => {
      // Navigate to adjudication page with the item selected
      navigate(`/adjudication?selected=${data.item.id}`);
    },
    onError: () => {
      // If creation fails (item might already exist), just go to adjudication
      navigate('/adjudication');
    },
    onSettled: () => {
      setReviewingMatch(null);
    },
  });

  const handleReviewMatch = (relatedEntityId: number, matchKey?: string) => {
    setReviewingMatch(relatedEntityId);
    const currentEntityId = parseInt(entityId!, 10);
    // Always put smaller entity ID first for consistency
    const [id1, id2] = currentEntityId < relatedEntityId
      ? [currentEntityId, relatedEntityId]
      : [relatedEntityId, currentEntityId];
    createAdjudicationMutation.mutate({ entityId1: id1, entityId2: id2, matchKey });
  };

  const handleViewWhyRelated = async (relatedEntityId: number) => {
    // If already loaded, just toggle
    if (whyRelatedData[relatedEntityId]) {
      setViewingWhyRelated(viewingWhyRelated === relatedEntityId ? null : relatedEntityId);
      return;
    }

    setViewingWhyRelated(relatedEntityId);
    try {
      const currentEntityId = parseInt(entityId!, 10);
      const data = await api.getWhyEntities(currentEntityId, relatedEntityId);
      setWhyRelatedData(prev => ({ ...prev, [relatedEntityId]: data }));
    } catch (error) {
      console.error('Failed to fetch why related data:', error);
    }
  };

  const { data: entity, isLoading, error } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.getEntity(parseInt(entityId!, 10)),
    enabled: !!entityId,
  });

  // Fetch lineage data
  const { data: lineageData } = useQuery({
    queryKey: ['lineage', entityId],
    queryFn: async () => {
      const response = await fetch(`/api/v1/lineage/${entityId}`);
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!entityId,
  });

  // Fetch merge history
  const { data: mergeHistoryData } = useQuery({
    queryKey: ['merge-history', entityId],
    queryFn: () => api.getEntityMergeHistory(parseInt(entityId!, 10)),
    enabled: !!entityId,
  });

  // Get entity IDs that were merged INTO this entity
  const mergedIntoThisEntityIds = mergeHistoryData?.merges
    ?.filter(m => m.mergedIntoEntityId === parseInt(entityId!, 10))
    ?.map(m => m.mergedEntityId) || [];

  // Fetch details of merged entities to show their records
  const { data: mergedEntitiesData } = useQuery({
    queryKey: ['merged-entities', entityId, mergedIntoThisEntityIds],
    queryFn: async () => {
      const uniqueIds = [...new Set(mergedIntoThisEntityIds)];
      const results = await Promise.all(
        uniqueIds.map(id => api.getEntity(id).catch(() => null))
      );
      return results.filter(Boolean);
    },
    enabled: mergedIntoThisEntityIds.length > 0,
  });

  // Get entity IDs that have been approved for merge (to filter from possible matches)
  const approvedMergeEntityIds = new Set(
    mergeHistoryData?.merges?.flatMap(m => [m.mergedEntityId, m.mergedIntoEntityId]) || []
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="w-32 h-5" />
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-3">
                <Skeleton className="w-64 h-8" />
                <Skeleton className="w-40 h-6" />
                <div className="flex gap-2">
                  <Skeleton className="w-24 h-6 rounded-full" />
                  <Skeleton className="w-20 h-6 rounded-full" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="w-40 h-6" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-3/4 h-4" />
              <Skeleton className="w-1/2 h-4" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="w-32 h-6" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-2/3 h-4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="space-y-4">
        <Link to="/entities" className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to search
        </Link>
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Entity not found or error loading data.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resolved = entity.resolvedEntity;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <Link to="/entities" className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors group">
          <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
          Back to search
        </Link>
      </motion.div>

      {/* Entity Header - Gold Record */}
      <motion.div variants={itemVariants}>
        <Card className="overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500" />
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/50 dark:to-primary-800/50 rounded-xl shadow-sm">
                <User className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{resolved.bestName}</h1>
                  <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" fill="currentColor" />
                    GOLD RECORD
                  </span>
                </div>
                <div className="flex items-center flex-wrap gap-4 mt-3">
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Entity ID:</span>
                    <span className="font-mono font-bold text-gray-900 dark:text-white text-lg">{entity.entityId}</span>
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 hidden sm:block" title="This ID is the stable reference that links all source records for this resolved entity">
                    Stable entity reference from MERLIN
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Badge variant="info" className="px-3 py-1">
                    {resolved.recordCount} linked records
                  </Badge>
                  {resolved.dataSources.map((ds) => (
                    <Badge key={ds} variant="default" className="px-3 py-1">{ds}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Business Identifiers */}
      {entity.linkedRecords.some((r) => r.rawData.LOYALTY_ID || r.rawData.IS_DAP_FLAG) && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Business Identifiers</h2>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Loyalty IDs */}
                {(() => {
                  const loyaltyIds = entity.linkedRecords
                    .filter((r) => r.rawData.LOYALTY_ID)
                    .map((r) => ({
                      id: String(r.rawData.LOYALTY_ID),
                      source: r.dataSource,
                    }));
                  const uniqueIds = [...new Set(loyaltyIds.map((l) => l.id))];

                  if (loyaltyIds.length === 0) return null;

                  return (
                    <div className={`p-4 rounded-xl transition-colors ${uniqueIds.length > 1 ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loyalty Account(s)</span>
                        {uniqueIds.length > 1 && (
                          <Badge variant="warning">DUPLICATE DETECTION</Badge>
                        )}
                      </div>
                      <div className="space-y-2">
                        {loyaltyIds.map((l, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-gray-900 dark:text-white font-medium">{l.id}</span>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">({l.source})</span>
                          </div>
                        ))}
                      </div>
                      {uniqueIds.length > 1 && (
                        <p className="mt-3 text-xs text-yellow-700 dark:text-yellow-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Multiple loyalty accounts detected - possible duplicate registration
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* DAP Status - IS_DAP_FLAG: '00' = not confirmed, '01' = confirmed DAP */}
                {(() => {
                  const dapRecords = entity.linkedRecords.filter(
                    (r) => r.rawData.IS_DAP_FLAG === '01'
                  );

                  if (dapRecords.length === 0) return null;

                  return (
                    <div className="p-4 rounded-xl bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-red-500 rounded-full">
                          <Shield className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-red-700 dark:text-red-400">DAP Status</span>
                      </div>
                      <p className="text-xl font-bold text-red-800 dark:text-red-300">CONFIRMED</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                        This person is confirmed in the Denied Access Program
                      </p>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Merge History */}
      {(() => {
        // Deduplicate merge history by entity pair
        const uniqueMerges = mergeHistoryData?.merges?.filter((merge, index, self) =>
          index === self.findIndex(m =>
            m.mergedEntityId === merge.mergedEntityId && m.mergedIntoEntityId === merge.mergedIntoEntityId
          )
        ) || [];

        return uniqueMerges.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-green-400 to-green-600" />
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <GitMerge className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Merge History</h2>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {uniqueMerges.map((merge) => {
                  const isMergedInto = merge.mergedIntoEntityId === entity.entityId;
                  return (
                    <motion.div
                      key={merge.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-900 dark:text-green-300">
                            {isMergedInto ? (
                              <>
                                Entity <Link to={`/entities/${merge.mergedEntityId}`} className="font-mono underline hover:text-green-700 dark:hover:text-green-200">{merge.mergedEntityId}</Link> was merged INTO this entity
                              </>
                            ) : (
                              <>
                                This entity was merged INTO Entity <Link to={`/entities/${merge.mergedIntoEntityId}`} className="font-mono underline hover:text-green-700 dark:hover:text-green-200">{merge.mergedIntoEntityId}</Link>
                              </>
                            )}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-green-700 dark:text-green-400">
                            <span>By: {merge.decidedBy}</span>
                            <span>On: {new Date(merge.decidedAt).toLocaleDateString()}</span>
                          </div>
                          {merge.notes && (
                            <p className="mt-2 text-sm text-green-800 dark:text-green-300 italic">"{merge.notes}"</p>
                          )}
                        </div>
                        <Badge variant="success">MERGED</Badge>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      );
      })()}

      {/* Entity Attributes */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Best Attributes</h2>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              {resolved.bestAddress && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</dt>
                    <dd className="text-gray-900 dark:text-white mt-1">
                      {resolved.bestAddress.line1}
                      {resolved.bestAddress.city && `, ${resolved.bestAddress.city}`}
                      {resolved.bestAddress.state && `, ${resolved.bestAddress.state}`}
                      {resolved.bestAddress.postalCode && ` ${resolved.bestAddress.postalCode}`}
                    </dd>
                  </div>
                </div>
              )}
              {resolved.bestDOB && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date of Birth</dt>
                    <dd className="text-gray-900 dark:text-white mt-1">{resolved.bestDOB}</dd>
                  </div>
                </div>
              )}
              {resolved.emails.length > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Mail className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</dt>
                    <dd className="text-gray-900 dark:text-white mt-1">{resolved.emails.join(', ')}</dd>
                  </div>
                </div>
              )}
              {!resolved.bestAddress && !resolved.bestDOB && resolved.emails.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">No additional attributes available</p>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All Names</h2>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {resolved.allNames.map((name, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <User className="h-4 w-4 text-gray-400" />
                  {name}
                  {i === 0 && <Badge variant="info" className="ml-auto text-xs">Primary</Badge>}
                </motion.li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </motion.div>

      {/* Data Lineage Section */}
      {lineageData && lineageData.sourceBreakdown && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <GitBranch className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Data Lineage</h2>
              </div>
            </CardHeader>
            <CardContent>
              {/* Source Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {lineageData.sourceBreakdown.map((source: { dataSource: string; recordCount: number; isExclusionSource: boolean }, index: number) => (
                  <motion.div
                    key={source.dataSource}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-xl border transition-colors ${
                      source.isExclusionSource
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {source.isExclusionSource ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : (
                        <FileText className="h-4 w-4 text-blue-500" />
                      )}
                      <span className={`text-sm font-medium ${
                        source.isExclusionSource ? 'text-red-700 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'
                      }`}>
                        {source.dataSource}
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{source.recordCount}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {source.isExclusionSource ? 'Exclusion List' : 'Source'} Records
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Cross-Source Analysis */}
              {lineageData.lineage && lineageData.lineage.length > 1 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Match Analysis</h3>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Total Records:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{lineageData.lineage.length}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Data Sources:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{lineageData.sourceBreakdown.length}</span>
                    </div>
                    {lineageData.hasExclusionMatch && (
                      <Badge variant="danger" className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Matched to Exclusion List
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Linked Records */}
      {(() => {
        // Combine records from this entity and merged entities
        const allRecords: Array<{
          record: typeof entity.linkedRecords[0];
          fromMergedEntity: number | null;
          mergedEntityName: string | null;
        }> = [
          // Records from this entity
          ...entity.linkedRecords.map(record => ({
            record,
            fromMergedEntity: null,
            mergedEntityName: null,
          })),
          // Records from merged entities
          ...(mergedEntitiesData || []).filter((e): e is NonNullable<typeof e> => e !== null).flatMap(mergedEntity =>
            mergedEntity.linkedRecords.map(record => ({
              record,
              fromMergedEntity: mergedEntity.entityId,
              mergedEntityName: mergedEntity.resolvedEntity.bestName,
            }))
          ),
        ];

        const validMergedEntities = (mergedEntitiesData || []).filter((e): e is NonNullable<typeof e> => e !== null);

        return (
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Linked Source Records</h2>
                  </div>
                  {validMergedEntities.length > 0 && (
                    <Badge variant="success" className="flex items-center gap-1">
                      <GitMerge className="h-3 w-3" />
                      Includes {validMergedEntities.reduce((sum, e) => sum + e.linkedRecords.length, 0)} merged
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Record ID is the unique identifier from the source file (e.g., row number from CSV). Click a row to see the full source data.
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-8"></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Record ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Loyalty ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Match Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Origin</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ingested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800/50">
                      {allRecords.map(({ record, fromMergedEntity, mergedEntityName }, index) => {
                        const isExclusion = EXCLUSION_SOURCES.includes(record.dataSource);
                        const isMerged = fromMergedEntity !== null;
                        const recordKey = `${record.dataSource}-${record.recordId}${isMerged ? `-merged-${fromMergedEntity}` : ''}`;
                        const isExpanded = expandedRecords.has(recordKey);
                        const loyaltyId = record.rawData?.LOYALTY_ID || record.rawData?.Universal_Player_ID;

                        return (
                          <React.Fragment key={recordKey}>
                            <motion.tr
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: index * 0.03 }}
                              onClick={() => toggleRecord(recordKey)}
                              className={`cursor-pointer transition-colors ${
                                isExclusion
                                  ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                                  : isMerged
                                    ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <td className="px-4 py-3 text-sm">
                                <motion.div
                                  animate={{ rotate: isExpanded ? 90 : 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                </motion.div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`inline-flex items-center gap-1 font-medium ${isExclusion ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                  {isExclusion && <AlertTriangle className="h-3 w-3" />}
                                  {record.dataSource}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">{record.recordId}</td>
                              <td className="px-4 py-3 text-sm">
                                {loyaltyId ? (
                                  <Badge variant="info" className="font-mono text-xs">
                                    {String(loyaltyId)}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <ConfidenceBadge score={record.matchScore} />
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {isMerged ? (
                                  <Link
                                    to={`/entities/${fromMergedEntity}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1"
                                  >
                                    <Badge variant="success" className="flex items-center gap-1">
                                      <GitMerge className="h-3 w-3" />
                                      From {fromMergedEntity}
                                    </Badge>
                                  </Link>
                                ) : (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Original</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {new Date(record.ingestedAt).toLocaleDateString()}
                              </td>
                            </motion.tr>
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.tr
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="bg-gray-50 dark:bg-gray-800"
                                >
                                  <td colSpan={7} className="px-4 py-4">
                                    <div className="ml-8">
                                      <div className="flex items-center gap-2 mb-3">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Source Record Data</h4>
                                        {isMerged && (
                                          <Badge variant="success" className="text-xs">
                                            Merged from Entity {fromMergedEntity}: {mergedEntityName}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 max-h-64 overflow-y-auto">
                                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                          {Object.entries(record.rawData || {})
                                            .filter(([key]) => !key.startsWith('_'))
                                            .map(([key, value]) => (
                                              <div key={key} className="flex py-1">
                                                <dt className="font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0">{key}:</dt>
                                                <dd className="text-gray-900 dark:text-white break-all">
                                                  {value === null || value === undefined || value === '' || value === 'NULL'
                                                    ? <span className="text-gray-400 dark:text-gray-500 italic">empty</span>
                                                    : String(value)}
                                                </dd>
                                              </div>
                                            ))}
                                        </dl>
                                      </div>
                                    </div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })()}

      {/* Related Entities */}
      {(() => {
        // Filter out possible matches that have been approved for merge
        const filteredPossibleMatches = entity.possibleMatches.filter(
          m => !approvedMergeEntityIds.has(m.entityId)
        );

        return (filteredPossibleMatches.length > 0 || entity.possibleRelationships.length > 0) && (
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                      <HelpCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Related Entities</h2>
                  </div>
                  {filteredPossibleMatches.length > 0 && (
                    <Link to="/adjudication">
                      <Button variant="ghost" size="sm" leftIcon={<Scale className="h-4 w-4" />}>
                        Go to Adjudication
                      </Button>
                    </Link>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {filteredPossibleMatches.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="warning" className="flex items-center gap-1">
                        <HelpCircle className="h-3 w-3" />
                        Possible Matches
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        (Entities that may be the same person)
                      </span>
                    </div>
                    <div className="space-y-3">
                      {filteredPossibleMatches.map((related, index) => (
                        <motion.div
                          key={related.entityId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <Link
                                to={`/entities/${related.entityId}`}
                                className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                              >
                                {related.resolvedName || 'Unknown'}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                              <div className="flex items-center gap-3 mt-1 text-sm">
                                <span className="text-gray-500 dark:text-gray-400">Entity {related.entityId}</span>
                                {related.dataSources?.length > 0 && (
                                  <span className="text-gray-400 dark:text-gray-500">
                                    {related.dataSources.join(', ')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleReviewMatch(related.entityId, related.matchKey)}
                              disabled={reviewingMatch === related.entityId}
                              isLoading={reviewingMatch === related.entityId}
                            >
                              Review Match
                            </Button>
                          </div>
                          {related.matchKey && (
                            <div className="mt-3 pt-3 border-t border-yellow-200 dark:border-yellow-800">
                              <div className="flex items-center flex-wrap gap-2 text-sm">
                                <span className="text-gray-500 dark:text-gray-400">Why they match:</span>
                                <code className="font-mono bg-white dark:bg-gray-800 px-2 py-0.5 rounded text-xs text-gray-700 dark:text-gray-300">
                                  {related.matchKey}
                                </code>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {related.matchKey.includes('+NAME') && <Badge variant="default">Similar name</Badge>}
                                {related.matchKey.includes('+DOB') && <Badge variant="default">Same DOB</Badge>}
                                {related.matchKey.includes('+ADDRESS') && <Badge variant="default">Similar address</Badge>}
                                {related.matchKey.includes('+SSN') && <Badge variant="default">Same SSN</Badge>}
                                {related.matchKey.includes('+PHONE') && <Badge variant="default">Same phone</Badge>}
                                {related.matchKey.includes('+EMAIL') && <Badge variant="default">Same email</Badge>}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {entity.possibleRelationships.length > 0 && (
                  <div className={filteredPossibleMatches.length > 0 ? 'pt-6 border-t border-gray-200 dark:border-gray-700' : ''}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="info" className="flex items-center gap-1">
                        <GitBranch className="h-3 w-3" />
                        Possible Relationships
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        (Related but likely different people - e.g., family, business associates)
                      </span>
                    </div>
                    <div className="space-y-3">
                      {entity.possibleRelationships.map((related, index) => {
                        const whyData = whyRelatedData[related.entityId] as { matchInfo?: { whyKey?: string }; featureComparison?: Array<{ featureType: string; entity1Value: string | null; entity2Value: string | null; score: number }> } | undefined;
                        const isExpanded = viewingWhyRelated === related.entityId;

                        return (
                          <motion.div
                            key={related.entityId}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <Link
                                  to={`/entities/${related.entityId}`}
                                  className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                                >
                                  {related.resolvedName || 'Unknown'}
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                                <div className="flex items-center gap-3 mt-1 text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Entity {related.entityId}</span>
                                  {related.dataSources?.length > 0 && (
                                    <span className="text-gray-400 dark:text-gray-500">
                                      {related.dataSources.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleViewWhyRelated(related.entityId)}
                                leftIcon={<HelpCircle className="h-4 w-4" />}
                              >
                                {isExpanded ? 'Hide Why' : 'Why Related'}
                              </Button>
                            </div>
                            {related.matchKey && (
                              <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                                <div className="flex items-center flex-wrap gap-2 text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Related because:</span>
                                  <code className="font-mono bg-white dark:bg-gray-800 px-2 py-0.5 rounded text-xs text-gray-700 dark:text-gray-300">
                                    {related.matchKey}
                                  </code>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {related.matchKey.includes('+NAME') && <Badge variant="default">Similar name</Badge>}
                                  {related.matchKey.includes('+DOB') && <Badge variant="default">Same DOB</Badge>}
                                  {related.matchKey.includes('+ADDRESS') && <Badge variant="info">Same address</Badge>}
                                  {related.matchKey.includes('+SSN') && <Badge variant="default">Same SSN</Badge>}
                                  {related.matchKey.includes('+PHONE') && <Badge variant="info">Same phone</Badge>}
                                  {related.matchKey.includes('+EMAIL') && <Badge variant="default">Same email</Badge>}
                                </div>
                              </div>
                            )}
                            <AnimatePresence>
                              {isExpanded && whyData && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800"
                                >
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Why These Entities Are Related
                                  </h4>
                                  {whyData.matchInfo?.whyKey && (
                                    <div className="mb-3 p-2 bg-white dark:bg-gray-800 rounded-lg">
                                      <span className="text-xs text-gray-500 dark:text-gray-400">Match Key: </span>
                                      <code className="font-mono text-xs text-gray-700 dark:text-gray-300">{whyData.matchInfo.whyKey}</code>
                                    </div>
                                  )}
                                  {whyData.featureComparison && whyData.featureComparison.length > 0 && (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-sm">
                                        <thead>
                                          <tr className="text-xs text-gray-500 dark:text-gray-400">
                                            <th className="text-left py-1 pr-4">Attribute</th>
                                            <th className="text-left py-1 pr-4">This Entity</th>
                                            <th className="text-left py-1 pr-4">Related Entity</th>
                                            <th className="text-left py-1">Score</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {whyData.featureComparison.map((feature, i) => (
                                            <tr key={i} className="border-t border-blue-100 dark:border-blue-900">
                                              <td className="py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">{feature.featureType}</td>
                                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{feature.entity1Value || '—'}</td>
                                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{feature.entity2Value || '—'}</td>
                                              <td className="py-2">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                  feature.score >= 90 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                  feature.score >= 70 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                }`}>
                                                  {feature.score}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        );
      })()}
    </motion.div>
  );
}

function ConfidenceBadge({ score }: { score: number }) {
  let className = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  let barColor = 'bg-gray-400';

  if (score >= 95) {
    className = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    barColor = 'bg-green-500';
  } else if (score >= 80) {
    className = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    barColor = 'bg-yellow-500';
  } else if (score >= 60) {
    className = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    barColor = 'bg-orange-500';
  } else {
    className = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    barColor = 'bg-red-500';
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${className}`}>
        {score}%
      </span>
      <div className="hidden sm:block w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full ${barColor} rounded-full`}
        />
      </div>
    </div>
  );
}
